// SPDX-License-Identifier: AGPL-3.0-or-later

import { useParams } from 'react-router';
import { Button } from '@intakeform/ui';
import { useQuestionnaire } from '../../api/questionnaires.js';
import { useAutofillJob } from '../../api/autofill.js';
import { qrToDraftAnswers, statusLabel } from './helpers.js';

/**
 * Phase 1 print view. Renders a flat, print-friendly summary of every
 * answer and lets the doctor trigger window.print(). Phase 3 may add a
 * server-rendered PDF via /api/autofill/:id/pdf.
 */
export function AutofillPrintRoute() {
  const { id } = useParams<{ id: string }>();
  const job = useAutofillJob(id);
  const form = useQuestionnaire(job.data?.questionnaireId);

  if (job.isLoading) return <p className="muted">Loading…</p>;
  if (job.error || !job.data) return <div className="error-banner">Failed to load.</div>;
  if (form.isLoading) return <p className="muted">Loading form…</p>;
  if (form.error || !form.data) return <div className="error-banner">Failed to load form.</div>;

  const j = job.data;
  const answers = qrToDraftAnswers(j.draftQr);

  const flatItems: { label: string; value: string }[] = [];
  const walk = (items: { linkId: string; type: string; text?: string; item?: unknown[] }[]) => {
    for (const item of items) {
      if (item.type === 'group') {
        walk((item.item ?? []) as typeof items);
        continue;
      }
      if (item.type === 'display') continue;
      const raw = answers[item.linkId];
      flatItems.push({
        label: item.text ?? item.linkId,
        value: raw === undefined || raw === null ? '—' : String(raw),
      });
    }
  };
  walk((form.data.resource.item ?? []) as Parameters<typeof walk>[0]);

  return (
    <div className="space-y-6">
      <header className="space-y-2 print:hidden">
        <h1 className="text-2xl font-bold tracking-tight">Print preview</h1>
        <p className="text-sm text-muted-foreground">
          Status: <strong data-testid="autofill-print-status">{statusLabel(j.status)}</strong>
          {j.publishedAt !== null
            ? ` · Published to EMR at ${new Date(j.publishedAt).toLocaleString()}`
            : null}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            type="button"
            onClick={() => {
              window.print();
            }}
          >
            Print
          </Button>
          <Button
            type="button"
            variant="secondary"
            data-testid="autofill-download-pdf"
            onClick={() => {
              // Anchor + click triggers the browser download; the
              // server sets Content-Disposition so the file name
              // matches across browsers.
              window.location.assign(`/api/autofill/${String(j.id)}/pdf`);
            }}
          >
            Download PDF
          </Button>
        </div>
      </header>

      <article className="space-y-4 max-w-3xl mx-auto bg-surface border border-border rounded-md p-6">
        <h2 className="text-xl font-bold">{form.data.resource.title}</h2>
        <p className="text-sm text-muted-foreground">
          Patient: {j.emrContextSnapshot.patient.displayName} · EMR id{' '}
          {j.emrContextSnapshot.patient.id}
        </p>
        <dl className="grid grid-cols-3 gap-y-3 gap-x-4">
          {flatItems.map((row, i) => (
            <div key={String(i)} className="contents">
              <dt className="font-medium col-span-1">{row.label}</dt>
              <dd className="col-span-2">{row.value}</dd>
            </div>
          ))}
        </dl>
      </article>
    </div>
  );
}
